// utils/contactService.ts
/**
 * Interface for contact form details
 */
export interface ContactDetails {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  gdprConsent: boolean;
  category?: string;
  department?: string;
  urgency?: 'low' | 'medium' | 'high';
}

/**
 * Opens the default email client with a contact form email
 * @param contactDetails - The contact details object
 * @returns boolean - Returns true if successful
 */
export const sendContactEmail = (
  contactDetails: ContactDetails
): boolean => {
  try {
    const {
      name,
      email,
      phone,
      subject,
      message,
      gdprConsent,
      category = 'General Inquiry',
      department = 'General',
      urgency = 'medium'
    } = contactDetails;

    // Format urgency for display
    const urgencyMap = {
      'low': '📋 Low',
      'medium': '⚠️ Medium',
      'high': '🚨 High'
    };

    const urgencyDisplay = urgencyMap[urgency] || '⚠️ Medium';

    // Email content
    const emailSubject = `Contact Form: ${subject} - ${category}`;
    
    const body = `Dear Nordisk Support Team,

I would like to get in touch regarding the following:

CONTACT DETAILS:
────────────────
Name: ${name}
Email: ${email}
Phone: ${phone}
Subject: ${subject}
Category: ${category}
Department: ${department}
Urgency: ${urgencyDisplay}
GDPR Consent: ${gdprConsent ? '✅ Given' : '❌ Not given'}

MESSAGE:
────────────────
${message}

I look forward to hearing from you soon.

Best regards,
${name}
${email}
${phone}

────────────────
This contact form was submitted via the Nordisk Support website on ${new Date().toLocaleDateString('en-GB')}`;

    // Encode for URL
    const encodedSubject = encodeURIComponent(emailSubject);
    const encodedBody = encodeURIComponent(body);
    
    // Create mailto link
    const mailtoLink = `mailto:info@nordisksupport.com?subject=${encodedSubject}&body=${encodedBody}`;
    
    // Open email client
    window.open(mailtoLink, '_blank');
    
    return true;
  } catch (error) {
    console.error('Error opening email client:', error);
    return false;
  }
};

/**
 * Validates contact form data
 */
export const validateContactForm = (data: ContactDetails): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!data.name.trim()) {
    errors.name = 'Name is required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Invalid email format';
  }

  if (!data.phone.trim()) {
    errors.phone = 'Phone number is required';
  }

  if (!data.subject.trim()) {
    errors.subject = 'Subject is required';
  }

  if (!data.message.trim()) {
    errors.message = 'Message is required';
  } else if (data.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters';
  }

  if (!data.gdprConsent) {
    errors.gdprConsent = 'You must accept the GDPR terms';
  }

  return errors;
};